import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from '@tanstack/react-router'
import { LoginModal } from '@/components/login/LoginModal'
import { ROUTES } from '@/constants/routeConstants'

interface HeaderProps {
  isLight?: boolean
}

export const Header = ({ isLight }: HeaderProps) => {
  const { t } = useTranslation()
  const [showIsConnected, setIsConnected] = useState(false)
  const [showIsSalesRepresentative, _setIsSalesRepresentative] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showLogin, setShowLogin] = useState(false)

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const navigationLinks = [
    {
      key: 'cleverMate',
      label: t('header.cleverMate'),
      show: showIsConnected,
    },
    {
      key: 'myNetwork',
      label: t('header.myNetwork'),
      show: showIsConnected,
    },
    {
      key: 'myProfile',
      label: t('header.myProfile'),
      show: !showIsSalesRepresentative && showIsConnected,
    },
    {
      key: 'contactUs',
      label: t('header.contactUs'),
      show: !showIsConnected,
    },
  ]

  const renderNavLinks = (isMobile: boolean) => (
    <>
      {navigationLinks
        .filter((link) => link.show)
        .map((link) => (
          <Link to={ROUTES.CONTACT_US} key={link.key}>
            <span
              key={link.key}
              className={`${
                isMobile ? 'py-2 cursor-pointer' : 'py-[5px] cursor-pointer'
              } ${isLight ? 'dark-navlink ' : 'light-navlink'}`}
            >
              {link.label}
            </span>
          </Link>
        ))}
    </>
  )

  const connectionButton = (isMobile: boolean) => (
    <button
      className={`flex items-center justify-center px-5 rounded-xl transition font-bold gap-2  ${isLight ? 'bg-gradient-orange-fuschia text-white' : 'bg-white text-black'} border border-transparent cursor-pointer hover:shadow-1 ${
        isMobile ? 'self-start w-full py-2 mt-2' : 'self-center w-fit py-1.5'
      }`}
      onClick={() =>
        showIsConnected ? setIsConnected(false) : setShowLogin(true)
      }
    >
      {t(showIsConnected ? 'header.disconnection' : 'header.connection')}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="17"
        height="13"
        viewBox="0 0 17 13"
        fill="none"
      >
        <path
          d="M14.75 6.20703L15.4571 5.49992L16.1642 6.20703L15.4571 6.91414L14.75 6.20703ZM1 7.20703C0.447715 7.20703 0 6.75932 0 6.20703C0 5.65475 0.447715 5.20703 1 5.20703V6.20703V7.20703ZM9.25 0.707031L9.95711 -7.55191e-05L15.4571 5.49992L14.75 6.20703L14.0429 6.91414L8.54289 1.41414L9.25 0.707031ZM14.75 6.20703L15.4571 6.91414L9.95711 12.4141L9.25 11.707L8.54289 10.9999L14.0429 5.49992L14.75 6.20703ZM14.75 6.20703V7.20703H1V6.20703V5.20703H14.75V6.20703Z"
          fill="currentColor"
        />
      </svg>
    </button>
  )

  return (
    <>
      <header
        className={`fixed z-50 w-full px-6 md:px-12 py-[21.5px] shadow-1 ${isLight ? 'bg-white' : 'bg-gradient-orange-fuschia'}`}
      >
        <div className="flex flex-row justify-between items-center">
          <Link to={`${ROUTES.ROOT}`}>
            <img
              className="block w-20 h-[39px] md:w-24 md:h-[47px]"
              src={`/img/cleverlink-logo-${isLight ? 'color' : 'white'}.svg`}
            />
          </Link>

          {/* Desktop Navigation */}
          <div
            className={`hidden md:flex flex-row text-[18px] gap-8 items-center`}
          >
            {renderNavLinks(false)}
            {connectionButton(false)}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden flex flex-col justify-center items-center w-8 h-8 ${isLight ? 'text-black' : 'text-white'}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-6 h-0.5 bg-current transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-45 translate-y-1.5' : ''}`}
            ></span>
            <span
              className={`block w-6 h-0.5 bg-current my-1 transition-opacity duration-300 ${isMobileMenuOpen ? 'opacity-0' : ''}`}
            ></span>
            <span
              className={`block w-6 h-0.5 bg-current transition-transform duration-300 ${isMobileMenuOpen ? '-rotate-45 -translate-y-1.5' : ''}`}
            ></span>
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <nav
            className={`flex flex-col gap-4 pt-6 ${isLight ? 'text-black' : 'text-white'} text-[16px]`}
          >
            {renderNavLinks(true)}
            {connectionButton(true)}
          </nav>
        </div>
      </header>
      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onConnect={() => setIsConnected(true)}
      />
    </>
  )
}
